import React, {Component, ReactElement} from 'react';

import moment from "moment";
import {TableRowProps} from "../TableRow";
import {FRIENDLY_DATE_FORMAT} from "../../utils/Utils";
import TableCell from "../TableCell";
import Table, {TableColumn, TableItem} from "../Table";
import ISchoolUsageSummary, {IUsageArea} from "../../models/usage-analytics/ISchoolUsageSummary";
import AreaUsageCalculationExplanations from './AreaUsageCalculationExplanations';
import { CommandBarButton } from 'office-ui-fabric-react';
import { gradeToStatusColour } from './GradeColourConverter';

interface UsageResultsTableProps {
    results: ISchoolUsageSummary[]
    enableLinks: boolean;
}

interface UsageResultsTableState {
    showExplanation: boolean;
}

const buildSortForArea = (usageArea: IUsageArea): (a: TableItem, b: TableItem) => number =>
{
    const sort = (a: ISchoolUsageSummary, b: ISchoolUsageSummary): number => {
        
        const resultA = a.results[usageArea];
        if (resultA?.markPercentage === null) {
            return -1;
        }
        const resultB = b.results[usageArea];
        if (resultB?.markPercentage === null) {
            return 1;
        }

        return resultA.markPercentage - resultB.markPercentage;
    };

    return sort as (a: TableItem,b: TableItem) => number;
}

const COLUMNS: TableColumn[] = [
    {
        title: "School",
        propertyPath: "school.schoolName",
        enableSort: true,
        flex: 1
    },
    {
        title: "Subject Usage",
        propertyPath: "area.subject",
        enableSort: true,
        sortFunc: buildSortForArea(IUsageArea.subject),
        flex: 1
    },
    {
        title: "Assignment Usage",
        propertyPath: "area.assignment",
        enableSort: true,
        sortFunc: buildSortForArea(IUsageArea.assignment),
        flex: 1
    },
    {
        title: "Notebook Usage",
        propertyPath: "area.notebook",
        enableSort: true,
        sortFunc: buildSortForArea(IUsageArea.notebook),
        flex: 1
    },
    {
        title: "Student Folder Usage",
        propertyPath: "area.studentFolders",
        enableSort: true,
        sortFunc: buildSortForArea(IUsageArea.studentFolders),
        flex: 1
    },
    {
        title: "Home Site Usage",
        propertyPath: "area.homeSite",
        enableSort: true,
        sortFunc: buildSortForArea(IUsageArea.homeSite),
        flex: 1
    },
    {
        title: "Staff Site Usage",
        propertyPath: "area.staffSite",
        enableSort: true,
        sortFunc: buildSortForArea(IUsageArea.staffSite),
        flex: 1
    },
    {
        title: "Training Site Usage",
        propertyPath: "area.trainingSite",
        enableSort: true,
        sortFunc: buildSortForArea(IUsageArea.trainingSite),
        flex: 1
    }
];

const rowPropsForItem = (item: ISchoolUsageSummary,enableRowLink: boolean): TableRowProps => {
    
    return {
        enableRowLink: enableRowLink,
        openInNewTab: true,
        to: `/cdb-admin/usage/${item.school.tenantId}/${item.school.schoolCode}/${item.datasetId}`,
        extraClassName: "usage-result-table__row"
    }
}

const cellForColumn = (instance: ISchoolUsageSummary, column: TableColumn, columnIndex: number): ReactElement<TableCell> | undefined => {

    if(column.propertyPath == "school.schoolName")
    {
        let creationDateString = moment(instance.datasetCreationDate).format(FRIENDLY_DATE_FORMAT);
        let creationDate: JSX.Element | undefined = undefined;

        const daysSince = moment().diff(moment(instance.datasetCreationDate), 'days')
        if(daysSince > 30)
        {
            creationDate = (<div className="pill pill--amber pill--tiny">{creationDateString}</div>);
        }
        else
        {
            creationDate = (<span className={"text-extra-detail"}>{creationDateString}</span>)
        }

        return (
            <TableCell flex={column.flex} className={`usage-result-cell--school`}>
                {instance.school.schoolName}
                {creationDate}
            </TableCell>
        )
    }

    if(column.propertyPath.startsWith("area."))
    {
        const usageArea = column.propertyPath.split(".")[1] as IUsageArea;
        const areaResult = instance.results[usageArea];
        
        if(areaResult === null || areaResult.grade == "NoData")
        {
            return (
                <TableCell flex={column.flex} className={`usage-result-cell usage-result-cell--grey`}>
                    <span></span>
                    <span className={"text-extra-detail"}>No Data</span>
                </TableCell>
            )
        }

        return (
            <TableCell flex={column.flex} className={`usage-result-cell usage-result-cell--${gradeToStatusColour(areaResult.grade)}`}>
                <span>{areaResult.markPercentage}%</span>
                <span className={"text-extra-detail"}>{areaResult.grade}</span>
            </TableCell>
        );
    }

}


export class UsageResultsTable extends Component<UsageResultsTableProps, UsageResultsTableState> {
    static displayName = UsageResultsTable.name;

    constructor(props: UsageResultsTableProps) {
        super(props);

        this.state = {
            showExplanation: false
        };
    }

    componentDidMount(): void {

    }


    componentDidUpdate(prevProps: Readonly<UsageResultsTableProps>, prevState: Readonly<UsageResultsTableState>, snapshot?: any): void {

    }

    private buildOutputViewer(title: boolean): JSX.Element | undefined {

        if (!title) {
            return undefined;
        } 

        return (
            <AreaUsageCalculationExplanations >
                <CommandBarButton
                    iconProps={{ iconName: "Cancel" }}
                    text="Close"
                    onClick={() => this.setState({ showExplanation: !this.state.showExplanation })}
                /></AreaUsageCalculationExplanations>
        )
    }

    render(): JSX.Element {
        return (
            <div>
                {this.buildOutputViewer(this.state.showExplanation)}
                <Table
                    className={"usage-result-table cdb-table--enable-hover cdb-table--sticky-header"}
                    title={"Usage"}
                    items={this.props.results}
                    columns={COLUMNS}
                    enableSort={true}
                    rowPropsForItem={(item) => rowPropsForItem(item as ISchoolUsageSummary, this.props.enableLinks)}
                    cellForColumn={cellForColumn}
                >
                    <CommandBarButton
                        iconProps={{ iconName: "info" }}
                        text={"Calculation info"}
                        onClick={() => this.setState({showExplanation: !this.state.showExplanation })}
                    />
                </Table>
            </div>
        );
    }
}